/**
 * Call an async function with exponential-backoff retry for transient failures.
 *
 * A failure is considered transient if it's a network error or a 5xx/429
 * response. Auth errors, validation errors, and other 4xx responses are NOT
 * retried so we don't mask real bugs or spam the backend.
 *
 * @param {Function} fn - zero-arg async function to call
 * @param {Object} [opts]
 * @param {number} [opts.retries=2] - number of retries after the first attempt
 * @param {number} [opts.baseDelayMs=400] - base delay for exponential backoff
 * @param {Function} [opts.isTransient] - override for transient-error detection
 * @returns {Promise<any>}
 */
export async function withRetry(fn, opts = {}) {
  const { retries = 2, baseDelayMs = 400, isTransient = defaultIsTransient } = opts;
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === retries || !isTransient(err)) {
        throw err;
      }
      const delay = baseDelayMs * Math.pow(2, attempt);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastErr;
}

function defaultIsTransient(err) {
  if (!err) return false;
  const code = err.code || '';
  // Firebase functions client uses 'unavailable' / 'deadline-exceeded' /
  // 'internal' / 'resource-exhausted' for transient conditions.
  if (
    code === 'unavailable' ||
    code === 'deadline-exceeded' ||
    code === 'resource-exhausted' ||
    code === 'internal'
  ) {
    return true;
  }
  // Network errors thrown by fetch typically have no status.
  const message = String(err.message || '').toLowerCase();
  if (
    message.includes('failed to fetch') ||
    message.includes('network error') ||
    message.includes('networkerror')
  ) {
    return true;
  }
  // HTTP-style status on err.status or err.httpErrorCode
  const status = err.status || err.httpErrorCode?.status;
  if (status === 429 || (status >= 500 && status < 600)) {
    return true;
  }
  return false;
}

/**
 * Translate a raw error into a short, user-friendly message. Falls back to
 * a generic string rather than surfacing a technical stack trace.
 */
export function toUserMessage(err, fallback = 'Something went wrong. Please try again.') {
  if (!err) return fallback;
  const code = err.code || '';
  if (code === 'unauthenticated' || code === 'permission-denied') {
    return 'You need to sign in again to do that.';
  }
  if (code === 'resource-exhausted') {
    return 'We\'re a little overloaded right now. Please try again in a moment.';
  }
  if (code === 'deadline-exceeded' || code === 'unavailable') {
    return 'The server is taking too long to respond. Please check your connection and try again.';
  }
  if (code === 'not-found') {
    return 'We couldn\'t find what you were looking for.';
  }
  const msg = String(err.message || '').trim();
  // Hide obviously technical messages (stack traces, framework internals)
  if (!msg || /\bat\s[^\s]+\s\(/.test(msg) || msg.length > 200) {
    return fallback;
  }
  return msg;
}
